import { Fragment, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { appModules } from "../../modules";
import type { SidebarSection } from "../../modules/types";
import { cx } from "../../ui/cx";
import { Icon } from "../../ui/Icon";
import { Menu, MenuHeader, MenuLink } from "../../ui/Menu";
import { isEntryActive, mergeSidebar, type PlacedEntry, visibleSidebar } from "../nav";
import { useShellContext } from "../session";
import { s } from "../strings";
import { readFlag, writeFlag } from "./persist";
import styles from "./Sidebar.module.css";

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onNavigate: () => void;
}

const SHOW_MORE_KEY = "sidebarShowMore";

export function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onNavigate }: SidebarProps) {
  const ctx = useShellContext();
  const entries = useMemo(() => visibleSidebar(mergeSidebar(appModules), ctx), [ctx]);
  const { pathname, search } = useLocation();
  const [showMore, setShowMore] = useState(() => readFlag(SHOW_MORE_KEY));
  const extra = entries.filter((e) => e.section === "SHOW_MORE");
  // Keep the active Tier-2 item visible even when SHOW MORE is folded, so the user sees where they are.
  const extraActive = extra.some((e) => isEntryActive(e, pathname));

  const toggleShowMore = () => {
    setShowMore((v) => {
      writeFlag(SHOW_MORE_KEY, !v);
      return !v;
    });
  };

  const renderSection = (section: SidebarSection, label?: string) => {
    const items = entries.filter((e) => e.section === section);
    if (items.length === 0) return null;
    return (
      <Fragment key={section}>
        {label && !collapsed && (
          <div className={styles.sectionLabel}>
            <span>{label}</span>
          </div>
        )}
        {items.map((e) => (
          <NavItem
            key={e.id}
            entry={e}
            active={isEntryActive(e, pathname)}
            collapsed={collapsed}
            search={search}
            onNavigate={onNavigate}
          />
        ))}
      </Fragment>
    );
  };

  return (
    <aside
      className={cx(styles.aside, collapsed && styles.collapsed, mobileOpen && styles.mobileOpen)}
      aria-label="Main navigation"
    >
      <nav className={styles.nav}>
        {renderSection("MAIN")}
        {renderSection("ANALYZE", s.sectionAnalyze)}
        {renderSection("MANAGE", s.sectionManage)}
        {extra.length > 0 && (
          <button
            type="button"
            className={styles.item}
            aria-expanded={showMore}
            aria-label={collapsed ? (showMore ? s.showLess : s.showMore) : undefined}
            onClick={toggleShowMore}
          >
            <span className={styles.iconBox}>
              <Icon name={showMore ? "chevron-up" : "chevron-down"} />
            </span>
            {!collapsed && <span className={styles.label}>{showMore ? s.showLess : s.showMore}</span>}
          </button>
        )}
        {(showMore || extraActive) &&
          extra
            .filter((e) => showMore || isEntryActive(e, pathname))
            .map((e) => (
              <NavItem
                key={e.id}
                entry={e}
                active={isEntryActive(e, pathname)}
                collapsed={collapsed}
                search={search}
                onNavigate={onNavigate}
              />
            ))}
      </nav>
      <button
        type="button"
        className={styles.toggler}
        aria-label={s.sidebarCollapse}
        aria-pressed={collapsed}
        onClick={onToggleCollapsed}
      >
        <Icon name={collapsed ? "chevrons-right" : "chevrons-left"} size={12} strokeWidth={2} />
      </button>
    </aside>
  );
}

interface NavItemProps {
  entry: PlacedEntry;
  active: boolean;
  collapsed: boolean;
  search: string;
  onNavigate: () => void;
}

function NavItem({ entry, active, collapsed, search, onNavigate }: NavItemProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Report links carry the current range so switching report type keeps the dates (live reports-nav).
  const range = useMemo(() => {
    const p = new URLSearchParams(search);
    const start = p.get("start");
    const end = p.get("end");
    return start && end ? `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}` : "";
  }, [search]);

  return (
    <>
      <Link
        ref={ref}
        to={entry.flyout ? `${entry.path}${range}` : entry.path}
        className={cx(styles.item, active && styles.active, entry.flyout && styles.hasFlyout)}
        aria-current={active ? "page" : undefined}
        aria-label={collapsed ? entry.label : undefined}
        aria-haspopup={entry.flyout ? "menu" : undefined}
        aria-expanded={entry.flyout ? flyoutOpen : undefined}
        onClick={(e) => {
          if (entry.flyout) {
            // Clockify navigates to the default report and opens the flyout in one click.
            e.preventDefault();
            navigate(`${entry.path}${range}`);
            setFlyoutOpen((v) => !v);
            return;
          }
          onNavigate();
        }}
      >
        <span className={styles.iconBox}>
          <Icon name={entry.icon} label={entry.alt} className={styles.icon} />
        </span>
        {!collapsed && <span className={styles.label}>{entry.label}</span>}
        {entry.flyout && !collapsed && <Icon name="chevron-right" size={16} className={styles.chevron} />}
      </Link>
      {entry.flyout && (
        <Menu
          open={flyoutOpen}
          onClose={() => setFlyoutOpen(false)}
          anchorRef={ref}
          placement="right-start"
          offset={12}
          width={140}
          aria-label={entry.label}
          className={styles.flyout}
        >
          {entry.flyout.map((g) => (
            <Fragment key={g.header}>
              <MenuHeader>{g.header}</MenuHeader>
              {g.items.map((it) => (
                <MenuLink key={it.path} to={`${it.path}${range}`} active={pathname === it.path}>
                  {it.label}
                </MenuLink>
              ))}
            </Fragment>
          ))}
        </Menu>
      )}
    </>
  );
}
